/**
 * NAVARASAM — Add-on services (Falls & Pico, Kucchu, Gift wrapping)
 * ============================================================================
 * Update-safe build. Added file only; no Shopify-owned file is edited.
 * Loaded by blocks/nv-addon-services.liquid (product pages) and
 * sections/nv-addon-sync.liquid (cart page).
 *
 * WHAT THIS DELIBERATELY DOES NOT USE
 * -----------------------------------
 * An earlier version hooked `@theme/events` (CartAddEvent / CartUpdateEvent).
 * Horizon 4.1.5 removed both, so that code silently stopped adding add-ons —
 * the shopper picked Falls & Pico and nothing was added. To avoid repeating
 * that, this file touches only surfaces Shopify documents:
 *
 *   - DOM clicks on the add-to-cart and cart remove buttons
 *   - the Cart AJAX API (/cart/add.js, /cart/change.js, /cart.js)
 *   - the event NAME "shopify:cart:lines-update" (listened for, never built)
 *
 * It never imports a theme module and never constructs a Shopify event object,
 * so a theme update cannot take it out the way the last one did.
 *
 * WHY HORIZON ALWAYS RENDERS, NEVER US
 * ------------------------------------
 * Every cart change a shopper can see is finished by Horizon, so the drawer,
 * the row list, the subtotal and the cart bubble all come out correct with no
 * DOM patching and no money formatting on our side — which is what went wrong
 * when we tried to fix the count by hand.
 *
 * THE TWO CART FLOWS
 * ------------------
 *   Adding    - cancel the click, add the add-on lines, then replay the click
 *               so Horizon adds the saree last and re-renders.
 *   Removing  - do NOT cancel. Horizon removes a cart line by its INDEX
 *               ({line: 2, quantity: 0}), so deleting an add-on first shifts
 *               the lines below it and Horizon deletes the wrong one. Instead
 *               it removes the clicked line itself, and afterwards we click the
 *               orphaned add-on's own remove button for it — one at a time,
 *               waiting between each for Shopify's re-render to land, because
 *               Horizon's next line index comes from that markup. Every removal
 *               and every render is Horizon's, so rows, subtotal and the cart
 *               bubble cannot disagree — the bug was that the add-on vanished
 *               while the count still counted it.
 *
 * Both listeners sit on WINDOW, not document. Horizon handles these clicks from
 * a document-level capture listener registered before this file loads; capture
 * starts at the window, so a window listener is the only one guaranteed to run
 * first. Measured: a document listener here ran after Horizon's and could not
 * cancel it.
 *
 * WHY SYNC IS SUPPRESSED DURING OUR OWN WORK
 * ------------------------------------------
 * Our own /cart/add.js call fires shopify:cart:lines-update. Without a guard,
 * syncAddons ran while the saree was still being added, saw an add-on whose
 * saree was not in the cart yet, and deleted it as an orphan — silently losing
 * the add-on the shopper had just chosen. addInFlightUntil holds sync off.
 *
 * HOW AN ADD-ON STAYS LINKED TO ITS SAREE
 * ---------------------------------------
 *   _nv_for = <variant id of the saree>
 * Leading underscore keeps it hidden from the shopper in cart and checkout.
 * It is intentionally not a unique per-add id: a unique id would stop identical
 * add-ons merging into one line, and each 1x line would then be synced up to
 * the saree's quantity, over-charging. Keying on the saree's variant lets
 * Shopify merge them and sync the merged line once.
 */

(() => {
  if (window.__NV_ADDON_V10__) return;
  window.__NV_ADDON_V10__ = true;

  const PROP_FOR = '_nv_for';
  const CART_EVENT = 'shopify:cart:lines-update';
  const SYNC_COOLDOWN_MS = 400;
  const WORK_GRACE_MS = 6000;

  const route = (name, fallback) => window.Theme?.routes?.[name] || fallback;

  /** While in the future, syncAddons() stands down (see header). */
  let workInFlightUntil = 0;

  /* ---------------------------------------------------------------- cart io */

  async function getCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) throw new Error('Could not read cart');
    return res.json();
  }

  async function addLine({ id, quantity, properties }) {
    const body = new FormData();
    body.append('id', String(id));
    body.append('quantity', String(quantity));
    for (const [key, value] of Object.entries(properties || {})) {
      body.append(`properties[${key}]`, String(value));
    }

    const res = await fetch(route('cart_add_url', '/cart/add.js'), {
      method: 'POST',
      body,
      headers: { Accept: 'application/json' },
    });
    const json = await res.json();
    if (json?.status) throw new Error(json.description || json.message || 'Add-on could not be added');
    return json;
  }

  async function changeLine(key, quantity) {
    const res = await fetch(route('cart_change_url', '/cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!res.ok) throw new Error('Cart change failed');
    return res.json();
  }

  /* ------------------------------------------------------------------ the UI */

  /** Reveal the extra-days note and the gift message box only when relevant. */
  function refreshConditionalUi() {
    document.querySelectorAll('[data-nv-addon]').forEach((block) => {
      const picked = block.querySelector('input[data-nv-service]:checked');
      const note = block.querySelector('[data-nv-note]');
      if (note) note.hidden = picked?.dataset.nvNeedsNote !== 'true';

      const giftToggle = block.querySelector('[data-nv-gift-toggle]');
      const giftBox = block.querySelector('[data-nv-gift-message]');
      if (giftToggle && giftBox) giftBox.hidden = !giftToggle.checked;
    });
  }

  document.addEventListener('change', (event) => {
    const el = event.target;
    if (el instanceof HTMLElement && el.matches('input[data-nv-service], [data-nv-gift-toggle]')) {
      refreshConditionalUi();
    }
  });

  /* --------------------------------------------- product page: add the add-ons */

  /** The add-on block belonging to the same section as this product form. */
  function blockForForm(form) {
    const component = form.closest('product-form-component');
    const sectionId = component?.dataset.sectionId;
    if (!sectionId) return null;
    return [...document.querySelectorAll('[data-nv-addon]')].find(
      (block) => block.dataset.nvSection === sectionId
    );
  }

  function readChoice(block, form) {
    const serviceId = block.querySelector('input[data-nv-service]:checked')?.value || '';
    const giftToggle = block.querySelector('[data-nv-gift-toggle]');
    const giftWanted = !!giftToggle?.checked;
    if (!serviceId && !giftWanted) return null;

    const mainVariantId = form.querySelector('[name="id"]')?.value || '';
    if (!mainVariantId) return null;

    return {
      mainVariantId,
      quantity: Math.max(1, parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10) || 1),
      serviceId,
      giftVariantId: giftWanted ? giftToggle.value : '',
      giftMessage: block.querySelector('[data-nv-gift-input]')?.value?.trim() || '',
      label: block.dataset.nvLabel || '',
    };
  }

  // Registered on WINDOW, not document: Horizon's own remove/add handling runs
  // from a document-level capture listener registered when its modules load,
  // which is before this file. Capture starts at the window, so a window
  // listener is the only way to be guaranteed first and actually cancel it.
  window.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest('button[name="add"]');
      if (!button) return;

      // Second pass: our replayed click. Let Horizon handle it untouched.
      if (button.__nvReplay) {
        button.__nvReplay = false;
        return;
      }

      const form = button.closest('form[data-type="add-to-cart-form"]');
      if (!form) return;

      const block = blockForForm(form);
      if (!block) return;

      const choice = readChoice(block, form);
      if (!choice) return; // nothing chosen: Horizon proceeds as normal

      event.preventDefault();
      event.stopImmediatePropagation();
      workInFlightUntil = Date.now() + WORK_GRACE_MS;

      (async () => {
        const { mainVariantId, quantity, serviceId, giftVariantId, giftMessage, label } = choice;
        try {
          if (serviceId) {
            await addLine({
              id: serviceId,
              quantity,
              properties: { [PROP_FOR]: mainVariantId, 'Service for': label },
            });
          }
          if (giftVariantId) {
            const properties = { [PROP_FOR]: mainVariantId, 'Gift wrap for': label };
            if (giftMessage) properties['Gift message'] = giftMessage;
            await addLine({ id: giftVariantId, quantity, properties });
          }
        } catch (error) {
          // Never block the saree because an add-on failed.
          console.warn('[nv-addon] add-on not added:', error);
        } finally {
          workInFlightUntil = Date.now() + WORK_GRACE_MS;
          button.__nvReplay = true;
          button.click();
        }
      })();
    },
    true
  );

  /* ------------------------------------ cart: remove add-ons with their saree */

  /**
   * Cart rows carry data-key of the form "<variantId>:<hash>", so the row a
   * remove button sits in tells us which saree is about to go.
   */
  function variantIdForRemoveButton(button) {
    const row = button.closest('[data-key]');
    const key = row?.getAttribute('data-key') || '';
    const variantId = key.split(':')[0];
    return { row, key, variantId: variantId || null };
  }

  /** The live remove button for a cart line, in the cart page or the drawer. */
  function removeButtonFor(key) {
    const row = document.querySelector(`[data-key="${window.CSS && CSS.escape ? CSS.escape(key) : key}"]`);
    return row ? row.querySelector('[class*="cart-items__remove"]') : null;
  }

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /** The line keys the rendered cart is currently showing, sorted. */
  function renderedCartKeys() {
    return [...document.querySelectorAll('[data-key]')]
      .filter((row) => row.querySelector('[class*="cart-items__remove"]'))
      .map((row) => row.getAttribute('data-key'))
      .sort()
      .join('|');
  }

  /**
   * Wait until the rendered cart matches the server's, and return that cart.
   *
   * This is the whole trick. Horizon takes a removed row out of the DOM at
   * once, before its request has even been answered, while the line index it
   * sends for the NEXT removal still comes from the markup Shopify last
   * rendered. So "the row is gone" does not mean "Horizon is ready" — measured:
   * clicking two add-ons in a row that way sent {line: 2} and {line: 3} against
   * a cart that by then had only two lines, and the wrong ones went. Waiting for
   * the render to land keeps every index Horizon sends correct.
   */
  async function waitForRenderedCart(timeoutMs = 8000) {
    const until = Date.now() + timeoutMs;
    while (Date.now() < until) {
      workInFlightUntil = Date.now() + WORK_GRACE_MS; // keep the safety net off
      try {
        const cart = await getCart();
        if (cart.items.map((line) => line.key).sort().join('|') === renderedCartKeys()) {
          return cart;
        }
      } catch {
        /* try again below */
      }
      await wait(250);
    }
    return null;
  }

  // On WINDOW for the same reason as the add-to-cart listener above.
  //
  // NOTE: this one does NOT cancel the click, and that is deliberate. Horizon
  // removes a cart line by its INDEX (`{line: 2, quantity: 0}`), so deleting an
  // add-on first shifts every line below it and Horizon then deletes the wrong
  // one — measured: the saree survived and the add-on went instead. So Horizon
  // removes the saree on its own click, and afterwards we hand it each orphaned
  // add-on row, one at a time, by clicking that row's own remove button. Every
  // removal and every re-render is Horizon's, so the rows, the subtotal and the
  // cart bubble can never disagree — which was the bug: the add-on vanished but
  // the count still counted it.
  window.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest('[class*="cart-items__remove"]');
      if (!button) return;

      // Our own handed-back click on an add-on row: leave it entirely alone.
      if (button.__nvReplay) {
        button.__nvReplay = false;
        return;
      }

      const { key, variantId } = variantIdForRemoveButton(button);
      if (!variantId) return;

      workInFlightUntil = Date.now() + WORK_GRACE_MS;

      (async () => {
        try {
          const before = await getCart();
          const owned = before.items.filter(
            (line) => String(line.properties?.[PROP_FOR] || '') === String(variantId) && line.key !== key
          );
          if (!owned.length) return;

          // Let Horizon finish removing the line the shopper clicked.
          let cart = await waitForRenderedCart();

          for (const line of owned) {
            // Something else may have taken it already (a second tab, say).
            if (cart && !cart.items.some((item) => item.key === line.key)) continue;

            const removeButton = removeButtonFor(line.key);
            if (!removeButton) {
              // No row to click — a page whose cart markup is not open, or a
              // render that never landed. Let the safety net finish the job.
              workInFlightUntil = 0;
              scheduleSync(200);
              return;
            }

            removeButton.__nvReplay = true;
            removeButton.click();
            cart = await waitForRenderedCart();
          }
        } catch (error) {
          console.warn('[nv-addon] remove sync skipped:', error);
          workInFlightUntil = 0;
          scheduleSync(200);
        } finally {
          // Let the safety net resume as soon as our work is done.
          workInFlightUntil = 0;
        }
      })();
    },
    true
  );

  /* ------------------------------------------------------------------- sync */

  let syncing = false;
  let lastSyncAt = 0;

  async function syncAddons() {
    if (syncing) return;
    if (Date.now() < workInFlightUntil) return;

    const now = Date.now();
    if (now - lastSyncAt < SYNC_COOLDOWN_MS) return;

    syncing = true;
    lastSyncAt = now;

    try {
      const cart = await getCart();

      const mainQty = new Map();
      const addons = [];

      for (const line of cart.items) {
        const belongsTo = line.properties?.[PROP_FOR];
        if (belongsTo) {
          addons.push({ line, belongsTo: String(belongsTo) });
        } else {
          const key = String(line.variant_id);
          mainQty.set(key, (mainQty.get(key) || 0) + line.quantity);
        }
      }

      if (!addons.length) return;

      let changed = false;

      for (const { line, belongsTo } of addons) {
        const target = mainQty.get(belongsTo) || 0;

        if (target === 0) {
          await changeLine(line.key, 0);
          changed = true;
          continue;
        }
        if (line.quantity === target) continue;

        try {
          await changeLine(line.key, target);
          changed = true;
        } catch {
          // A quantity rule on the add-on can refuse the change. Fall back to 1
          // rather than removing something the shopper asked for.
          if (line.quantity !== 1) {
            try {
              await changeLine(line.key, 1);
              changed = true;
            } catch {
              /* leave it; the cart is still valid */
            }
          }
        }
      }

      // This path is the safety net, not the main flow: it runs when the cart
      // changed somewhere we did not intercept, so Horizon has already
      // rendered and its markup is now stale. A reload is the only way to make
      // rows, subtotal and count agree without re-implementing Horizon's
      // rendering or its money formatting.
      if (changed) window.location.reload();
    } catch (error) {
      console.warn('[nv-addon] sync skipped:', error);
    } finally {
      syncing = false;
    }
  }

  /** Two attempts: the second clears the cooldown if the first was swallowed. */
  function scheduleSync(delay) {
    const first = delay || 500;
    setTimeout(syncAddons, first);
    setTimeout(syncAddons, first + 1500);
  }

  /* ------------------------------------------------------------------- boot */

  refreshConditionalUi();

  // Horizon re-renders product and cart markup in place, so re-check on change.
  new MutationObserver(refreshConditionalUi).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  // The platform event, caught wherever it lands. It does not reliably reach a
  // listener on document, so window and capture are covered too.
  document.addEventListener(CART_EVENT, () => scheduleSync(400), true);
  window.addEventListener(CART_EVENT, () => scheduleSync(400), true);

  window.addEventListener('pageshow', () => scheduleSync(400));
  syncAddons();
})();
